/**
 * Cache wrapper for the weather range endpoint. Wraps `API.Weather.range(...)` with a
 * `TtlCache` keyed by (quantized lat/lon, ISO date range). Survives page nav (e.g.
 * Robots → JobDiagnostics → back) so revisiting the same shift doesn't re-hit the
 * backend.
 *
 * TTL: 30 minutes. Weather data for archived dates (>5 days old) is immutable, so the
 * TTL is mostly for the rare case of viewing a recent shift whose archive backfilled
 * since the last fetch — we want fresh data eventually, but not on every page nav.
 */

import { TtlCache } from "../cache/ttlCache";
import { API } from "../api";
import type { WeatherGrid, WeatherRange } from "../api/routers/weather";

const RANGE_TTL_MS = 30 * 60 * 1000;
// Forecast grids mutate (Open-Meteo refreshes hourly) and the backend already caches for an
// hour; a 30-min client TTL keeps panning/toggling cheap without serving very stale data.
const GRID_TTL_MS = 30 * 60 * 1000;

/** Key format: `${lat_q},${lon_q},${startMs},${endMs}`. Lat/lon are quantized to 0.01°
 *  to match the backend's S3 cache key, so two callers with slightly-different raw coords
 *  still collide on the same entry. The fetcher parses the key back into args. */
const buildKey = (lat: number, lon: number, start: Date, end: Date): string => {
	const lq = Math.round(lat * 100) / 100;
	const lnq = Math.round(lon * 100) / 100;
	return `${lq},${lnq},${start.getTime()},${end.getTime()}`;
};

class CachedWeatherRange {
	private cache = new TtlCache<string, WeatherRange>(RANGE_TTL_MS, async (key) => {
		const [lat, lon, startMs, endMs] = key.split(",").map(Number);
		return API.Weather.range(lat, lon, new Date(startMs), new Date(endMs));
	});

	public peek(lat: number, lon: number, start: Date, end: Date): WeatherRange | null {
		return this.cache.peek(buildKey(lat, lon, start, end));
	}

	public get(lat: number, lon: number, start: Date, end: Date): Promise<WeatherRange> {
		return this.cache.get(buildKey(lat, lon, start, end));
	}

	public clear(): void {
		this.cache.clear();
	}
}

/** Key format: `${w_q},${s_q},${e_q},${n_q},${cols},${rows}`. The bbox corners are quantized
 *  to 0.1° to match the backend's grid cache key, so small map nudges still collide. The
 *  fetcher parses the key back into args. */
const buildGridKey = (west: number, south: number, east: number, north: number, cols: number, rows: number): string => {
	const q = (v: number) => Math.round(v * 10) / 10;
	return `${q(west)},${q(south)},${q(east)},${q(north)},${cols},${rows}`;
};

class CachedWeatherGrid {
	private cache = new TtlCache<string, WeatherGrid>(GRID_TTL_MS, async (key) => {
		const [west, south, east, north, cols, rows] = key.split(",").map(Number);
		return API.Weather.grid(west, south, east, north, cols, rows);
	});

	public peek(
		west: number,
		south: number,
		east: number,
		north: number,
		cols: number,
		rows: number,
	): WeatherGrid | null {
		return this.cache.peek(buildGridKey(west, south, east, north, cols, rows));
	}

	public get(
		west: number,
		south: number,
		east: number,
		north: number,
		cols: number,
		rows: number,
	): Promise<WeatherGrid> {
		return this.cache.get(buildGridKey(west, south, east, north, cols, rows));
	}

	public clear(): void {
		this.cache.clear();
	}
}

export class CachedWeatherAPI {
	public range = new CachedWeatherRange();
	public grid = new CachedWeatherGrid();
}
